import React, { useMemo } from "react";
import { Box } from "@mui/material";
import BuyerCompletionList from "../../BuyerCompletionList";

import LineChart from "../../LineChart";
import StatCard from "../../StatCard";
import {
  ContractAnalyticsResponse,
  ContractOverviewResponse,
  AvgCctByMonth,
} from "../../../../types/contracts";
import { FilterValues } from "../../FilterModal";
import styles from "./performanceDashboard.module.scss";

// Utility function to transform CCT data with year handling
const transformCctData = (
  data: AvgCctByMonth[]
): Array<{ month: string; avgCctDays: number }> => {
  if (!data || data.length === 0) return [];

  // Check if data spans multiple years
  const years = [...new Set(data.map((item) => item.year).filter(Boolean))];
  const hasMultipleYears = years.length > 1;

  return data.map((item) => ({
    month: hasMultipleYears && item.year ? `${item.month} ${item.year}` : item.month,
    avgCctDays: item.avgCctDays,
  }));
};

interface PerformanceDashboardProps {
  analyticsData: ContractAnalyticsResponse;
  overviewData: ContractOverviewResponse;
  filters?: FilterValues;
}

const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({
  analyticsData,
  overviewData,
  filters = {},
}) => {
  const completionData = analyticsData.completionByBuyer || [];

  const bestPerformer =
    completionData.length > 0
      ? completionData.reduce((best, current) =>
          current.completionPct > best.completionPct ? current : best,
        )
      : null;

  const expiringInThirtyDays =
    overviewData.secondaryCards?.expiringInThirtyDays || 0;

  const fastestCct = analyticsData.fastestCct;
  const fastestCctDays = fastestCct?.cctDays || 0;
  const fastestCctContract = fastestCct?.contractName || "N/A";

  // Check if any date filters are applied
  const hasDateFilters = [
    filters.start_date_with_sd,
    filters.end_date_with_sd,
    filters.start_date_with_ed,
    filters.end_date_with_ed,
    filters.start_date_with_cct_created_at,
    filters.end_date_with_cct_created_at,
    filters.start_date_with_cct_updated_at,
    filters.end_date_with_cct_updated_at,
  ].some(Boolean);

  // Transform CCT data with memoization for performance
  const transformedCctData = useMemo(
    () => transformCctData(analyticsData.avgCctByMonth || []),
    [analyticsData.avgCctByMonth]
  );

  return (
    <>
      <Box className={styles.fullWidthChart}>
        <BuyerCompletionList
          title="% Completion By Buyer"
          data={completionData}
          nameKey="buyerName"
          rateKey="completionPct"
          countKey="contractsCount"
          completedCountKey="completedCount"
          showThisYear={!hasDateFilters}
        />
      </Box>

      <Box className={styles.chartsGrid}>
        <LineChart
          title="Avg CCT (Calendar Days)"
          data={transformedCctData}
          xAxisKey="month"
          dataKey="avgCctDays"
          lineColor="#5b6b9e"
          legendLabel="Average CCT"
          valueUnit="days"
          showThisYear={!hasDateFilters}
        />
        {/* <LineChart
          title="Avg CCT - Signed Contracts"
          data={[]}
          xAxisKey="month"
          dataKey="avgCctDays"
          lineColor="#08ADC2"
          legendLabel="Signed CCT"
          valueUnit="days"
        /> */}
      </Box>

      <Box className={styles.statCardsGrid}>
        <StatCard
          type="performer"
          title="Best Performer"
          value={bestPerformer?.buyerName || "N/A"}
          subtitle={`${bestPerformer?.completionPct || 0}% completion rate`}
        />
        <StatCard
          type="fastest"
          title="Fastest CCT"
          value={`${fastestCctDays} Days`}
          subtitle={fastestCctContract}
        />
        {/* <StatCard
          type="attention"
          title="Needs Attention"
          value={String(expiringInThirtyDays)}
          subtitle="Expiring in 2 weeks"
        /> */}
      </Box>
    </>
  );
};

export default PerformanceDashboard;
